import { useState } from 'react'
import { auth } from '@/api/client'

/**
 * Minimal change-password dialog for the teacher portal. Teacher accounts are
 * created (and reset) with a temporary password by server/scripts/teacher-admin.js;
 * this is how the teacher replaces it with one of their own.
 */
export default function ChangePasswordModal({ email, onClose }: { email: string; onClose: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext]       = useState('')
  const [again, setAgain]     = useState('')
  const [busy, setBusy]       = useState(false)
  const [msg, setMsg]         = useState<{ ok: boolean; text: string } | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setMsg(null)
    if (next.length < 8)  return setMsg({ ok: false, text: 'New password must be at least 8 characters' })
    if (next !== again)   return setMsg({ ok: false, text: 'New passwords do not match' })
    if (next === current) return setMsg({ ok: false, text: 'New password must be different' })
    setBusy(true)
    try {
      await auth.changePassword(email, current, next)
      setMsg({ ok: true, text: 'Password changed. Use the new one next time you log in.' })
      setCurrent(''); setNext(''); setAgain('')
    } catch (err: any) {
      setMsg({ ok: false, text: err.message || 'Could not change password' })
    } finally {
      setBusy(false)
    }
  }

  const label: React.CSSProperties = { fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#818cf8', display: 'block', marginBottom: 4 }
  const field: React.CSSProperties = { fontFamily: 'monospace', fontSize: '13px', width: '100%', padding: '8px 10px',
                                       background: '#1e1b4b', border: '2px solid #4338ca', color: '#e0e7ff', borderRadius: 4, outline: 'none' }

  return (
    <div role="dialog" aria-modal="true" aria-label="Change password"
         onClick={onClose}
         style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 380, background: '#0f0f23', border: '2px solid #312e81',
                     borderRadius: 12, padding: 20, boxShadow: '0 0 40px rgba(99,102,241,0.25)' }}>
        <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '9px', color: '#fbbf24', marginBottom: 14 }}>
          🔑 CHANGE PASSWORD
        </p>
        <p style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#9ca3af', marginBottom: 14 }}>{email}</p>

        <label style={label}>CURRENT PASSWORD</label>
        <input type="password" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)} style={{ ...field, marginBottom: 12 }} />
        <label style={label}>NEW PASSWORD (8+ CHARACTERS)</label>
        <input type="password" autoComplete="new-password" value={next} onChange={e => setNext(e.target.value)} style={{ ...field, marginBottom: 12 }} />
        <label style={label}>NEW PASSWORD AGAIN</label>
        <input type="password" autoComplete="new-password" value={again} onChange={e => setAgain(e.target.value)} style={{ ...field, marginBottom: 14 }} />

        {msg && (
          <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', lineHeight: 1.6,
                      color: msg.ok ? '#34d399' : '#fca5a5', marginBottom: 12 }}>
            {msg.ok ? '✅ ' : '❌ '}{msg.text}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose}
            style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', padding: '8px 12px', borderRadius: 4,
                     background: '#1e1b4b', color: '#818cf8', border: '1px solid #312e81', cursor: 'pointer' }}>
            {msg?.ok ? 'DONE' : 'CANCEL'}
          </button>
          {!msg?.ok && (
            <button type="submit" disabled={busy}
              style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', padding: '8px 12px', borderRadius: 4,
                       background: '#4f46e5', color: '#fff', border: '2px solid #818cf8', cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? 'SAVING…' : 'SAVE'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
